/**
 * Database for Asset Classifications
 * Stores the classification hierarchy and codes
 */

const DB_NAME = 'asset_classification_db';
const DB_VERSION = '1.0';

/**
 * Initialize the database
 */
export const initClassificationDB = () => {
  try {
    const existing = localStorage.getItem(DB_NAME);
    if (!existing) {
      const initialDB = {
        _version: DB_VERSION,
        _createdAt: new Date().toISOString(),
        _updatedAt: new Date().toISOString(),
        classifications: [],
        assetModels: []
      };
      localStorage.setItem(DB_NAME, JSON.stringify(initialDB));
      return initialDB;
    }
    return JSON.parse(existing);
  } catch (e) {
    console.error('Error initializing classification database:', e);
    return {
      _version: DB_VERSION,
      _createdAt: new Date().toISOString(),
      _updatedAt: new Date().toISOString(),
      classifications: [],
      assetModels: []
    };
  }
};

/**
 * Generate Sub Type code based on name
 */
export const generateSubTypeCode = (subTypeName) => {
  if (!subTypeName || subTypeName.trim() === '') return '';
  
  const words = subTypeName.trim().split(/\s+/).filter(w => w.length > 0);
  
  if (words.length === 0) return '';
  
  if (words.length === 1) {
    // One word: first 3 letters
    return words[0].substring(0, 3).toUpperCase();
  } else if (words.length === 2) {
    // Two words: 2 letters from first word + 1 letter from second word
    const first = words[0].substring(0, 2).toUpperCase();
    const second = words[1].substring(0, 1).toUpperCase();
    return first + second;
  } else {
    // Three or more words: first letter of first three words
    return words.slice(0, 3).map(w => w.substring(0, 1).toUpperCase()).join('');
  }
};

/**
 * Get all asset models
 */
export const getAllAssetModels = () => {
  try {
    const db = initClassificationDB();
    return db.assetModels || [];
  } catch (e) {
    console.error('Error getting asset models:', e);
    return [];
  }
};

/**
 * Save asset model
 */
export const saveAssetModel = (modelData) => {
  try {
    const db = initClassificationDB();
    const allModels = db.assetModels || [];
    
    const existingIndex = allModels.findIndex(m => m.id === modelData.id);
    
    const model = {
      ...modelData,
      updatedAt: new Date().toISOString()
    };
    
    if (existingIndex >= 0) {
      model.createdAt = allModels[existingIndex].createdAt;
      allModels[existingIndex] = model;
    } else {
      model.id = model.id || generateId();
      model.createdAt = new Date().toISOString();
      allModels.push(model);
    }
    
    db.assetModels = allModels;
    db._updatedAt = new Date().toISOString();
    localStorage.setItem(DB_NAME, JSON.stringify(db));
    return model;
  } catch (e) {
    console.error('Error saving asset model:', e);
    throw e;
  }
};

/**
 * Save multiple asset models
 */
export const saveMultipleAssetModels = (models) => {
  try {
    models.forEach(model => {
      saveAssetModel(model);
    });
    return true;
  } catch (e) {
    console.error('Error saving multiple asset models:', e);
    return false;
  }
};

/**
 * Get unique classifications
 */
export const getUniqueClassifications = () => {
  try {
    const models = getAllAssetModels();
    const classifications = new Set();
    const types = new Set();
    const subTypes = new Set();
    
    models.forEach(model => {
      if (model.assetClassification) classifications.add(model.assetClassification);
      if (model.assetType) types.add(model.assetType);
      if (model.assetSubType) subTypes.add(model.assetSubType);
    });
    
    return {
      classifications: Array.from(classifications).sort(),
      types: Array.from(types).sort(),
      subTypes: Array.from(subTypes).sort()
    };
  } catch (e) {
    console.error('Error getting unique classifications:', e);
    return { classifications: [], types: [], subTypes: [] };
  }
};

/**
 * Export data for AI processing
 */
export const exportForAI = () => {
  try {
    const models = getAllAssetModels();
    return models.map(m => ({
      id: m.id,
      assetModel: m.assetModel,
      currentClassification: m.assetClassification || '',
      currentType: m.assetType || '',
      currentSubType: m.assetSubType || '',
      currentSubTypeCode: m.assetSubTypeCode || ''
    }));
  } catch (e) {
    console.error('Error exporting for AI:', e);
    return [];
  }
};

/**
 * Import AI suggestions
 */
export const importAISuggestions = (suggestions) => {
  try {
    const db = initClassificationDB();
    db.aiSuggestions = suggestions;
    db._updatedAt = new Date().toISOString();
    localStorage.setItem(DB_NAME, JSON.stringify(db));
    return true;
  } catch (e) {
    console.error('Error importing AI suggestions:', e);
    return false;
  }
};

/**
 * Get AI suggestions
 */
export const getAISuggestions = () => {
  try {
    const db = initClassificationDB();
    return db.aiSuggestions || [];
  } catch (e) {
    console.error('Error getting AI suggestions:', e);
    return [];
  }
};

/**
 * Clear AI suggestions
 */
export const clearAISuggestions = () => {
  try {
    const db = initClassificationDB();
    delete db.aiSuggestions;
    db._updatedAt = new Date().toISOString();
    localStorage.setItem(DB_NAME, JSON.stringify(db));
    return true;
  } catch (e) {
    console.error('Error clearing AI suggestions:', e);
    return false;
  }
};

/**
 * Generate unique ID
 */
const generateId = () => {
  return `am_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

